/**
 * Reading a customer address back as one line.
 *
 * The shop writes an address the way it would write it on a box — one block of
 * text, because that is how somebody says where they live and how a rider
 * reads it. `line1` holds that block.
 *
 * The separate barangay, city, province and postcode columns are older: the
 * form used to ask for each one in its own box. Records created that way still
 * carry them, so they are appended when present. Nothing writes them any more,
 * and the columns are left alone rather than being migrated away — a rebuild
 * of `customer_addresses` would clear `orders.address_id` on every order ever
 * delivered, which is a real cost for a tidier schema.
 */
export function formatAddress(address) {
    const line1 = address.line1?.trim() ?? '';
    const haystack = line1.toLowerCase();
    /*
     * A part `line1` already names is dropped rather than repeated.
     *
     * The old form asked for the city separately *and* people wrote it into the
     * street line anyway — of the shop's addresses that carry a separate part,
     * every single one already names it. Appending it blindly gives "845 Arnaiz
     * Avenue Makati City, Makati".
     */
    const extras = [address.barangay, address.city, address.province, address.postalCode]
        .map((part) => part?.trim())
        .filter((part) => Boolean(part))
        .filter((part) => !haystack.includes(part.toLowerCase()));
    const line = [line1, ...extras].filter(Boolean).join(', ');
    // A landmark is a direction, not part of the address, so it reads after it.
    const landmark = address.landmark?.trim();
    return landmark && !haystack.includes(landmark.toLowerCase()) ? `${line} (${landmark})` : line;
}
/**
 * The communities the shop delivers to, most specific first.
 *
 * Nearly all of these are villages inside one estate in Taguig, and together
 * they account for 181 of the 305 addresses on file. They are written the short
 * way the shop says them — "Cedar", not "Cedar Crest Acacia Estates".
 *
 * Mahogany Place carries its number: the three are far enough apart to matter
 * to whoever is driving. It is matched on "place" and "drive" but never on
 * "street", because "#14 Mahogany street" is in a different subdivision
 * altogether.
 */
const COMMUNITIES = [
    [/\bmahogany\s*place\s*([123])\b/i, 'Mahogany Place $1'],
    [/\bmp\s*([123])\b/i, 'Mahogany Place $1'],
    [/\bmahogany\s*(?:place|drive)\b/i, 'Mahogany Place'],
    [/\bcedar(?:\s*crest)?\b/i, 'Cedar'],
    [/\bverawood(?:\s*residences)?\b/i, 'Verawood'],
    [/\balder(?:\s*residences)?\b/i, 'Alder'],
    [/\broyal\s*palm(?:\s*residences)?\b/i, 'Royal Palm'],
    [/\brosewood(?:\s*pointe)?\b/i, 'Rosewood'],
    [/\b(?:the\s+)?birchwood\b/i, 'Birchwood'],
    [/\bivory\s*wood\b/i, 'Ivory Wood'],
    [/\bmulberry\s*place\b/i, 'Mulberry Place'],
    [/\bmaple\s*place\b/i, 'Maple Place'],
    [/\bgrace\s*residences\b/i, 'Grace Residences'],
    [/\bpanglao(?:\s*oasis)?\b/i, 'Panglao'],
    [/\bridgewood(?:\s*towers)?\b/i, 'Ridgewood'],
    [/\bm\s+residences\b/i, 'M Residences'],
];
/**
 * The estate and city, which the community column already implies.
 *
 * Only ever stripped from an address whose community was recognised. When it
 * wasn't, the line is all the rider has and every word of it stays.
 */
const REDUNDANT_TAIL = /[\s,.]*\b(?:acacia\s*estates?|taguig(?:\s*city)?|metro\s*manila|philippines)\b[\s,.]*/gi;
/**
 * Tidy one half of a split address.
 *
 * "Bldg." goes because the shop's own sheet says "321 Fennel", not "321 Fennel
 * Bldg." — but "St" stays, since in Mahogany Place the stop *is* a street and
 * a house number rather than a building and a unit.
 */
function tidy(part, stripTail) {
    return (stripTail ? part.replace(REDUNDANT_TAIL, ' ') : part)
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[,;]+$/, '')
        .replace(/\s+(?:bldg\.?|building)\s*$/i, '')
        .replace(/[,;.]+$/, '')
        .trim();
}
/**
 * Split a one-line address into where to drive and where to stop.
 *
 * The rider's sheet is grouped by community, so the community has to come out
 * of the line as its own column. There is no grammar to these lines — they were
 * typed by whoever took the order — so this matches the names the shop knows
 * and takes what comes before them.
 *
 * Checked against all 305 addresses on file: 181 land in a community, one of
 * those with nothing left over ("Ridgewood Towers" is the whole address), and
 * the remaining 124 are one-offs scattered across the metro that correctly come
 * back with no community at all. It is wrong often enough that every field on
 * the sheet stays editable, and right often enough to save writing the same
 * list out by hand every day.
 *
 * Feed it `formatAddress(address)`, not `line1`, so an address still carrying
 * the old separate city and barangay columns reads whole.
 */
export function splitDeliveryAddress(line) {
    // Collapsed first: one address on file has a line break in the middle of it.
    const text = String(line ?? '').replace(/\s+/g, ' ').trim();
    if (!text)
        return { community: '', detail: '' };
    for (const [pattern, name] of COMMUNITIES) {
        const match = pattern.exec(text);
        if (!match)
            continue;
        const community = name.replace('$1', match[1] ?? '').trim();
        const before = tidy(text.slice(0, match.index), true);
        /*
         * Usually the stop comes first and the community after it. When somebody
         * has written them the other way round — "Maple Place Spruce 110" — what
         * follows the community is the stop.
         */
        const after = tidy(text.slice(match.index + match[0].length), true);
        return { community, detail: before || after };
    }
    return { community: '', detail: tidy(text, false) };
}
/**
 * The communities in the order a rider would work through them.
 *
 * Everything down to Maple Place is inside one estate, so they sit together and
 * the three Mahogany Places follow in their own numbered run. The last few are
 * elsewhere in the city and come after it. Anything unrecognised sorts last,
 * which is right: those are the scattered one-offs, and a rider does them on the
 * way out or on the way back rather than in the middle of the estate.
 *
 * This is only a starting order. The whole point of the sheet is that whoever
 * is driving rearranges it.
 */
export const DELIVERY_COMMUNITIES = [
    'Cedar',
    'Verawood',
    'Alder',
    'Royal Palm',
    'Rosewood',
    'Birchwood',
    'Ivory Wood',
    'Mulberry Place',
    'Maple Place',
    'Mahogany Place 1',
    'Mahogany Place 2',
    'Mahogany Place 3',
    'Mahogany Place',
    'M Residences',
    'Grace Residences',
    'Panglao',
    'Ridgewood',
];
/** Where a community falls in the default round. Unknown names sort last. */
export function communityRank(community) {
    const at = DELIVERY_COMMUNITIES.indexOf(community);
    return at === -1 ? DELIVERY_COMMUNITIES.length : at;
}
//# sourceMappingURL=addresses.js.map